if (!customElements.get('mobile-product-bar')) {
  customElements.define(
    'mobile-product-bar',
    class MobileProductBar extends HTMLElement {
      constructor() {
        super();
        this.mobileMedia = window.matchMedia('(max-width: 749px)');
        this.productInfo = this.closest('product-info');
        this.price = this.querySelector('[data-mobile-product-price]');
        this.button = this.querySelector('[data-mobile-product-submit]');
      }

      connectedCallback() {
        if (!this.productInfo || !this.button) return;

        this.preventGalleryDrag = (event) => {
          if (event.target instanceof Element && event.target.closest('media-gallery')) event.preventDefault();
        };
        this.productInfo.addEventListener('dragstart', this.preventGalleryDrag, true);
        this.disableImageDrag();
        this.setupDirectionalGallery();

        this.button.addEventListener('click', this.handleStickySubmit);
        this.mobileMedia.addEventListener('change', this.handleViewportChange);

        this.formObserver = new MutationObserver(() => {
          this.disableImageDrag();
          this.syncFromMainForm();
        });
        this.formObserver.observe(this.productInfo, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        this.observeMainButton();
        this.syncFromMainForm();
      }

      disconnectedCallback() {
        this.productInfo?.removeEventListener('dragstart', this.preventGalleryDrag, true);
        this.button?.removeEventListener('click', this.handleStickySubmit);
        this.mobileMedia?.removeEventListener('change', this.handleViewportChange);
        this.formObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.teardownDirectionalGallery();
        document.documentElement.classList.remove('mobile-product-sticky-visible');
      }

      get mainButton() {
        return this.productInfo?.querySelector('.product-form__submit');
      }

      disableImageDrag() {
        this.productInfo?.querySelectorAll('media-gallery img, media-gallery .product__media').forEach((element) => {
          element.draggable = false;
          element.setAttribute('draggable', 'false');
          element.ondragstart = () => false;
        });
      }

      setupDirectionalGallery() {
        this.gallerySlider = this.productInfo?.querySelector('media-gallery [id^="Slider-Gallery-"]');
        if (!this.gallerySlider) return;

        this.handleGalleryTouchStart = (event) => {
          if (!this.mobileMedia.matches || event.touches.length !== 1) return;
          // Safari can preserve a vertical rubber-band offset on an overflow-x gallery.
          this.gallerySlider.scrollTop = 0;
          const touch = event.touches[0];
          this.galleryTouch = {
            x: touch.clientX,
            y: touch.clientY,
            scrollLeft: this.gallerySlider.scrollLeft,
            deltaX: 0,
            direction: null,
            moved: false,
          };
        };

        this.handleGalleryTouchMove = (event) => {
          if (!this.galleryTouch || event.touches.length !== 1) return;
          if (this.gallerySlider.scrollTop !== 0) this.gallerySlider.scrollTop = 0;
          const touch = event.touches[0];
          const deltaX = touch.clientX - this.galleryTouch.x;
          const deltaY = touch.clientY - this.galleryTouch.y;
          const distanceX = Math.abs(deltaX);
          const distanceY = Math.abs(deltaY);
          this.galleryTouch.deltaX = deltaX;

          if (!this.galleryTouch.direction) {
            if (Math.max(distanceX, distanceY) < 8) return;
            this.galleryTouch.direction = distanceX > distanceY * 1.15 ? 'horizontal' : 'vertical';
            if (this.galleryTouch.direction === 'horizontal') {
              this.gallerySlider.classList.add('product-gallery--touching');
            }
          }

          // A vertical or diagonal gesture belongs entirely to the page scroll.
          if (this.galleryTouch.direction !== 'horizontal') return;

          event.preventDefault();
          this.galleryTouch.moved = this.galleryTouch.moved || distanceX > 12;
          this.gallerySlider.scrollLeft = this.galleryTouch.scrollLeft - deltaX;
        };

        this.handleGalleryTouchEnd = () => {
          if (!this.galleryTouch) return;
          // Restore snapping first; re-enabling it after scrollTo() cancels the
          // destination animation in Safari/Chromium overflow galleries.
          this.gallerySlider.classList.remove('product-gallery--touching');

          if (this.galleryTouch.direction === 'horizontal') {
            const slides = Array.from(this.gallerySlider.querySelectorAll('.product__media-item'));
            const startingSlide = slides.reduce((nearest, slide) => {
              if (!nearest) return slide;
              return Math.abs(slide.offsetLeft - this.galleryTouch.scrollLeft) <
                Math.abs(nearest.offsetLeft - this.galleryTouch.scrollLeft)
                ? slide
                : nearest;
            }, null);
            const startingIndex = Math.max(0, slides.indexOf(startingSlide));
            const changeThreshold = Math.min(90, Math.max(48, this.gallerySlider.clientWidth * 0.18));
            let targetIndex = startingIndex;

            if (Math.abs(this.galleryTouch.deltaX) >= changeThreshold) {
              targetIndex += this.galleryTouch.deltaX < 0 ? 1 : -1;
            }
            targetIndex = Math.max(0, Math.min(slides.length - 1, targetIndex));
            const targetSlide = slides[targetIndex];

            if (targetSlide) {
              const targetLeft = targetSlide.offsetLeft;
              // Apply after WebKit has finished the native touch release; otherwise
              // it restores the last finger position and cancels our snap.
              window.setTimeout(() => {
                this.gallerySlider?.scrollTo({ left: targetLeft, behavior: 'auto' });
              });
            }
            if (this.galleryTouch.moved) this.suppressGalleryClickUntil = Date.now() + 400;
          }

          this.gallerySlider.scrollTop = 0;
          this.galleryTouch = null;
        };

        this.handleGalleryClick = (event) => {
          if (Date.now() >= (this.suppressGalleryClickUntil || 0)) return;
          event.preventDefault();
          event.stopPropagation();
        };

        this.gallerySlider.addEventListener('touchstart', this.handleGalleryTouchStart, { passive: true });
        this.gallerySlider.addEventListener('touchmove', this.handleGalleryTouchMove, { passive: false });
        this.gallerySlider.addEventListener('touchend', this.handleGalleryTouchEnd, { passive: true });
        this.gallerySlider.addEventListener('touchcancel', this.handleGalleryTouchEnd, { passive: true });
        this.gallerySlider.addEventListener('click', this.handleGalleryClick, true);
      }

      teardownDirectionalGallery() {
        if (!this.gallerySlider) return;
        this.gallerySlider.removeEventListener('touchstart', this.handleGalleryTouchStart);
        this.gallerySlider.removeEventListener('touchmove', this.handleGalleryTouchMove);
        this.gallerySlider.removeEventListener('touchend', this.handleGalleryTouchEnd);
        this.gallerySlider.removeEventListener('touchcancel', this.handleGalleryTouchEnd);
        this.gallerySlider.removeEventListener('click', this.handleGalleryClick, true);
        this.gallerySlider.classList.remove('product-gallery--touching');
      }

      observeMainButton() {
        this.visibilityObserver?.disconnect();
        const mainButton = this.mainButton;
        if (!mainButton) return;

        const rect = mainButton.getBoundingClientRect();
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        this.mainButtonVisible = visibleHeight / Math.max(rect.height, 1) >= 0.35;

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.mainButtonVisible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
            this.updateVisibility();
          },
          { threshold: [0, 0.35, 1] }
        );
        this.visibilityObserver.observe(mainButton);
      }

      getCurrentPrice() {
        const priceRoot = this.productInfo?.querySelector('[id^="price-"]');
        if (!priceRoot) return '';

        const prices = Array.from(priceRoot.querySelectorAll('.price-item')).filter(
          (element) => element.getClientRects().length > 0 && !element.closest('s')
        );
        return prices[prices.length - 1]?.textContent.trim() || priceRoot.textContent.trim();
      }

      syncFromMainForm() {
        const mainButton = this.mainButton;
        if (!mainButton) return;

        const currentPrice = this.getCurrentPrice();
        if (currentPrice && this.price.textContent.trim() !== currentPrice) this.price.textContent = currentPrice;

        const disabled =
          mainButton.disabled ||
          mainButton.getAttribute('aria-disabled') === 'true' ||
          mainButton.hasAttribute('disabled');
        if (this.button.disabled !== disabled) this.button.disabled = disabled;
        if (this.button.getAttribute('aria-disabled') !== String(disabled)) {
          this.button.setAttribute('aria-disabled', String(disabled));
        }

        const mainLabel = mainButton.querySelector('span:not(.sold-out-message)')?.textContent.trim();
        if (mainLabel && this.button.textContent.trim() !== mainLabel) this.button.textContent = mainLabel;
        this.updateVisibility();
      }

      updateVisibility() {
        const shouldShow = this.mobileMedia.matches && !this.mainButtonVisible;
        this.hidden = !shouldShow;
        document.documentElement.classList.toggle('mobile-product-sticky-visible', shouldShow);
      }

      handleStickySubmit = () => {
        const mainButton = this.mainButton;
        if (!mainButton || this.button.disabled) return;
        mainButton.click();
      };

      handleViewportChange = () => {
        this.updateVisibility();
      };
    }
  );
}
